import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { TableSettings } from 'components/TableSettings';
import { PriceListTableCount } from 'components/TotalCount/PriceListTableCount';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import type { VariableIdType } from 'pages/Catalogs/Common/types';
import { Button, cnMixSpace } from 'uikit';

import type { IDatasource } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import cn from 'classnames';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { useCallback, useState } from 'react';

import { useCatalogVariablesOtherColumns } from '../../hooks';
import type { CatalogVariablesOtherRow } from '../../types';

import Styles from './catalogVariablesOtherTable.module.css';

type TCatalogVariablesOtherTableProps = {
    tableRef: MutableRefObject<AgGridReact<CatalogVariablesOtherRow> | null>;
    datasource: IDatasource;
    fetchedCount: number;
    totalCount: number;
    onCreate: () => void;
    canCreateVariable: boolean;
    onEdit: (id: VariableIdType) => () => void;
    onView: (id: number) => () => void;
    onRemove: (id: Exclude<VariableIdType, undefined>) => () => void;
    catalogsRules?: IUseAccess['catalogs'];
    showSubsidiaryName?: boolean;
    catalogUniqueId?: string;
    globalOnly: boolean;
    methodology: ProjectMethodologyEnum;
    setFilterValues: Dispatch<SetStateAction<Record<string, string[]>>>;
};

export const CatalogVariablesOtherTable = ({
    tableRef,
    datasource,
    fetchedCount,
    totalCount,
    onCreate,
    canCreateVariable,
    onEdit,
    onView,
    onRemove,
    catalogsRules,
    showSubsidiaryName = false,
    catalogUniqueId,
    globalOnly,
    methodology,
    setFilterValues,
}: TCatalogVariablesOtherTableProps) => {
    const [isGridReady, setIsGridReady] = useState(false);

    const handleGridReady = useCallback(() => {
        setIsGridReady(true);
    }, []);

    const { columns, fullSetSettingsColumns, isColumnVisible, tableSettingsConfirm } = useCatalogVariablesOtherColumns({
        onEdit,
        onView,
        onRemove,
        catalogsRules,
        showSubsidiaryName,
        catalogUniqueId,
        globalOnly,
        methodology,
        setFilterValues,
    });

    return (
        <div className={cn(Styles.table, cnMixSpace({ mH: 'xl' }))}>
            <div className={Styles.header}>
                <div className={Styles.headerLeft}>
                    <Button
                        label="Создать переменную"
                        onClick={onCreate}
                        size="s"
                        view="secondary"
                        disabled={!canCreateVariable}
                    />
                    <PriceListTableCount totalCount={fetchedCount} totalItems={totalCount} />
                </div>
                <TableSettings
                    columns={fullSetSettingsColumns}
                    onConfirm={tableSettingsConfirm}
                    isColumnVisible={isColumnVisible}
                    showPinColumn
                    tableApi={isGridReady ? tableRef.current : null}
                />
            </div>
            <div className={Styles.grid}>
                <AgGridTable
                    ref={tableRef}
                    rowModelType="infinite"
                    datasource={datasource}
                    columnDefs={columns}
                    suppressCellFocus
                    headerHeight={40}
                    maxConcurrentDatasourceRequests={1}
                    maxBlocksInCache={50}
                    cacheBlockSize={50}
                    onGridReady={handleGridReady}
                    context={{
                        tableUniqueId: 'catalogVariablesOther',
                    }}
                />
            </div>
        </div>
    );
};

CatalogVariablesOtherTable.displayName = 'CatalogVariablesOtherTable';
